import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetchJson } from "@/lib/authenticatedFetch";
import type { SubscriptionResponse } from "@/types/subscription";
import { useQuery } from "@tanstack/react-query";

export const subscriptionKeys = {
  all: ["subscription"] as const,
  detail: (orgId: string) => [...subscriptionKeys.all, orgId] as const,
};

/**
 * Shared fetch function for subscription data.
 * Used by both useSubscription hook and prefetchQuery in selectOrg.
 */
export async function fetchSubscription(orgId: string) {
  const response = await authenticatedFetchJson<SubscriptionResponse>(
    `${V2_ENDPOINT}/v1/organizations/${orgId}/subscription`,
    { orgId },
  );
  return response.data;
}

/**
 * Hook for fetching subscription info (tier, limits, usage) for an org.
 */
export function useSubscription(organizationId?: string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: subscriptionKeys.detail(organizationId || ""),
    queryFn: () => fetchSubscription(organizationId!),
    enabled: !!organizationId,
    staleTime: 60_000, // 1 minute — subscription changes infrequently
    refetchInterval: options?.refetchInterval ?? 300_000, // Auto-refresh every 5 minutes
  });
}
